import React from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useFormik } from "formik";
import Select from 'react-select';

import SearchableDropdown from "../SearchableDropdown";
import StateDropDown from "../StateDropdown";
import CityDropdown from "../CityDropdown";
import { Enumeration } from "../../utils/interface";
import { formatEnumsForDropDown } from "../../utils/helperFunction";

interface NewPropertyFormProps {
  PropertyValidationSchema: any;
  propertyTypes: Enumeration[];
  createNewProperty: (propertyDetails: any) => void;
  fetchOwners: (searchTerm: string, pageIndex: number, pageSize: number) =>
    Promise<{ options: { value: number; label: string; }[], hasMore: boolean }>;
}

const NewPropertyForm: React.FC<NewPropertyFormProps> = ({
  PropertyValidationSchema,
  propertyTypes,
  createNewProperty,
  fetchOwners,
}) => {
  const formik = useFormik({
    initialValues: {
      isVerified: false,
      propertyTypeEnumId: undefined,
      propertyTitle: '',
      ownerId: undefined,
      mobile: '',
      description: '',
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      locality: '',
      pincode: '',
      stateISOCode: '',
      state: '',
      city: '',
    },
    validationSchema: PropertyValidationSchema,
    onSubmit: (values, { resetForm }) => {
      createNewProperty(values);
      resetForm();
    },
  });
  return (<>
    <Container>
      <h5 className="text-center font-weight-bold">New Property</h5>
      <Form onSubmit={formik.handleSubmit}>
        <Form.Group className="mb-3 mt-4" controlId="verified-property">
          <Form.Check
            type="checkbox"
            label={<span style={{ color: 'blue', fontSize: '18px', fontWeight: 'bold' }}>Verified Property</span>}
            checked={formik.values.isVerified}
            onChange={(e) => formik.setFieldValue('isVerified', e.target.checked)}
          />
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-3" controlId="property-type">
              <Form.Label><strong>Property Type</strong></Form.Label>
              <Select
                options={formatEnumsForDropDown(propertyTypes)}
                isSearchable
                placeholder=""
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    formik.setFieldValue('propertyTypeEnumId', selectedOption.value)
                  }
                }}
              />
              {formik.errors.propertyTypeEnumId && formik.touched.propertyTypeEnumId ? (
                <div className="text-danger">{formik.errors.propertyTypeEnumId}</div>
              ) : null}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3" controlId="owner">
              <Form.Label><strong>Owner</strong></Form.Label>
              <SearchableDropdown
                placeholder="Search owner..."
                fetchData={fetchOwners}
                onChange={(selectedOption) => formik.setFieldValue('ownerId', selectedOption?.value)}
                selectedData={null}
              />
              {formik.errors.ownerId && formik.touched.ownerId ? (
                <div className="text-danger">{formik.errors.ownerId}</div>
              ) : null}
            </Form.Group>
          </Col>
          <Col><Form.Group className="mb-3" controlId="owner-mobile">
            <Form.Label><strong>Mobile</strong></Form.Label>
            <Form.Control
              type="text"
              name="mobile"
              value={formik.values.mobile}
              onChange={formik.handleChange}
              isInvalid={!!(formik.touched.mobile && formik.errors.mobile)}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.mobile}
            </Form.Control.Feedback>
          </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3" controlId="property-title">
          <Form.Label><strong>Property Title</strong></Form.Label>
          <Form.Control
            type="text"
            name="propertyTitle"
            value={formik.values.propertyTitle}
            onChange={formik.handleChange}
            isInvalid={!!(formik.touched.propertyTitle && formik.errors.propertyTitle)}
          />
          <Form.Control.Feedback type="invalid">
            {formik.errors.propertyTitle}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" controlId="description">
          <Form.Label><strong>Description</strong></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="description"
            onChange={formik.handleChange}
          />
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-3" controlId="addressLine1">
              <Form.Label><strong>Address 01</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="addressLine1"
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.addressLine1 && formik.errors.addressLine1)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.addressLine1}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3" controlId="addressLine2">
              <Form.Label><strong>Address 02</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="addressLine2"
                onChange={formik.handleChange}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs="7">
            <Form.Group className="mb-3" controlId="landmark">
              <Form.Label><strong>Landmark</strong></Form.Label>
              <Form.Control
                type="text"
                name="landmark"
                onChange={formik.handleChange}
              />
            </Form.Group>
          </Col>
          <Col xs="5">
            <Form.Group className="mb-3" controlId="locality">
              <Form.Label><strong>Locality</strong></Form.Label>
              <Form.Control
                type="text"
                name="locality"
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.locality && formik.errors.locality)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.locality}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group className="mb-3" controlId="state">
              <StateDropDown
                selectedStateISOCode={formik.values.state}
                onStateChange={(selectedOption) => {
                  formik.setFieldValue('stateISOCode', selectedOption?.value);
                  formik.setFieldValue('state', selectedOption?.label);
                }}
              />
              {formik.errors.stateISOCode && formik.touched.stateISOCode ? (
                <div className="text-danger">{formik.errors.stateISOCode}</div>
              ) : null}
              {formik.errors.state && formik.touched.state ? (
                <div className="text-danger">{formik.errors.state}</div>
              ) : null}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3" controlId="city">
              <CityDropdown
                selectedCity={formik.values.city}
                stateCode={formik.values.stateISOCode}
                onCityChange={(selectedOption) => formik.setFieldValue('city', selectedOption?.value)}
              />
              {formik.errors.city && formik.touched.city ? (
                <div className="text-danger">{formik.errors.city}</div>
              ) : null}
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3" controlId="pincode">
              <Form.Label><strong>Pincode</strong></Form.Label>
              <Form.Control
                type="text"
                name="pincode"
                onChange={formik.handleChange}
                isInvalid={!!(formik.touched.pincode && formik.errors.pincode)}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.pincode}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" type="submit">
          Create
        </Button>
      </Form>
    </Container>
  </>);
};

export default NewPropertyForm;
